import { useEffect, useState } from 'react';
import { organizationAPI } from '../../services/api';
import { LoadError, OrgAnalytics, Organization, loadErrorOf } from './orgTypes';
import { addDaysYmd, athensDaysAgoYmd, athensYmd } from '../../utils/athensDate';

/**
 * Τα αναλυτικά της «Αρχικής» των κεντρικών. Η κατάσταση ζει στο OrgDashboard (όχι στην καρτέλα), όπως
 * πριν από τη νέα εμφάνιση: η περίοδος και το φίλτρο καταστημάτων μένουν όταν αλλάζεις καρτέλα, και ο
 * server ρωτιέται μόνο όταν αλλάξει ο οργανισμός, η περίοδος ή το φίλτρο — ίδιο endpoint, ίδιο query.
 */

export type OrgRangeKey = 'today' | '7' | '30' | '90' | 'custom';

/** Πόσες ημέρες ΠΡΙΝ από σήμερα αρχίζει κάθε έτοιμη περίοδος (σήμερα μετρά). */
const RANGE_BACK: Record<Exclude<OrgRangeKey, 'custom'>, number> = { today: 0, '7': 6, '30': 29, '90': 89 };

/**
 * Η περίοδος σε συγκεκριμένες ημερομηνίες — ΜΙΑ πηγή για τα αναλυτικά και τις εξαγωγές, ώστε να μη
 * διαφωνούν ποτέ. Ημερολογιακές μέρες ΕΛΛΑΔΑΣ: ως 24/10/2026 ήταν ημερομηνίες UTC (toISOString) και από
 * 00:00 ως 03:00 το «Σήμερα» ζητούσε το χθες.
 */
export function resolveOrgRange(range: OrgRangeKey, customFrom: string, customTo: string, now: Date | number = Date.now()): { from: string; to: string } {
  if (range === 'custom') return { from: customFrom, to: customTo };
  const to = athensYmd(now);
  return { from: addDaysYmd(to, -(RANGE_BACK[range] ?? 0)), to };
}

export interface OrgAnalyticsState {
  range: OrgRangeKey;
  setRange: (range: OrgRangeKey) => void;
  customFrom: string;
  customTo: string;
  setCustomRange: (from: string, to: string) => void;
  /** Απομόνωση καταστημάτων — κενό = όλα. Ισχύει στα αναλυτικά ΚΑΙ στις εξαγωγές. */
  filterStoreIds: number[];
  setFilterStoreIds: (ids: number[] | ((prev: number[]) => number[])) => void;
  data: OrgAnalytics | null;
  setData: (update: (prev: OrgAnalytics | null) => OrgAnalytics | null) => void;
  loading: boolean;
  error: LoadError | null;
  reload: () => void;
  resolve: () => { from: string; to: string };
}

export function useOrgAnalytics(selectedOrg: Organization | null): OrgAnalyticsState {
  const [range, setRange] = useState<OrgRangeKey>('30');
  // Προσαρμοσμένη περίοδος: προεπιλογή οι τελευταίες 30 ημέρες, ώστε η αλλαγή σε αυτήν να δείχνει κάτι αμέσως.
  const [customFrom, setCustomFrom] = useState<string>(() => athensDaysAgoYmd(29));
  const [customTo, setCustomTo] = useState<string>(() => athensYmd());
  const [filterStoreIds, setFilterStoreIds] = useState<number[]>([]);
  const [data, setDataState] = useState<OrgAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<LoadError | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!selectedOrg) return undefined;
    // Προσαρμοσμένη με «Από» μετά το «Έως» (ακόμη διαλέγει): καμία κλήση.
    if (range === 'custom' && customFrom > customTo) return undefined;
    let cancelled = false;
    const { from, to } = resolveOrgRange(range, customFrom, customTo);
    setLoading(true);
    organizationAPI.analytics(selectedOrg.id, from, to, filterStoreIds)
      .then(d => {
        if (cancelled) return;
        setDataState(d);
        setError(null);
      })
      .catch(err => {
        if (cancelled) return;
        setDataState(null);
        setError(loadErrorOf(err));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedOrg, range, customFrom, customTo, filterStoreIds, reloadTick]);

  return {
    range,
    setRange,
    customFrom,
    customTo,
    setCustomRange: (from, to) => {
      setCustomFrom(from);
      setCustomTo(to);
      setRange('custom');
    },
    filterStoreIds,
    setFilterStoreIds,
    data,
    setData: update => setDataState(prev => update(prev)),
    loading,
    error,
    reload: () => setReloadTick(n => n + 1),
    resolve: () => resolveOrgRange(range, customFrom, customTo),
  };
}
